import {diApi} from '@di-shared/api/di';
import {type ApiResponseEcoMileageResponseInfoLinkRtnData} from '@di-shared/api/di/__generated';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import {addDate, differenceInDay, parseDateISO, parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export enum ChallengePeriodType {
  ONE_YEAR,
  TWO_YEARS_OR_MORE,
  TODAY,
}

export enum ChallengeCalculationMethodType {
  REDUCTION_RATE,
  REDUCTION_AMOUNT,
}

export interface MileageBoundaryParams {
  challengeCalculationCase: ChallengeCalculationMethodType;
  challengePeriodCase: ChallengePeriodType;
  stdrDrvngDstnc: number;
  nowDrvngDstnc: number;
  drvngDstncRgsde: string;
  nowDrvngRgsde: string;
}

export enum ChallengeType {
  SEVEN,
  FIVE,
  TRREE,
  TWO,
  CHALLENGE_UNAVAILABLE,
  UPDATE_STATUS,
  ODOMETER_ERROR,
}

export interface MileageBoundary {
  seven: number;
  five: number;
  three: number;
  two: number;
  persistent: number;
}

function getChallengeBoundary({
  challengeCalculationCase,
  challengePeriodCase,
  stdrDrvngDstnc,
  nowDrvngDstnc,
  drvngDstncRgsde,
  nowDrvngRgsde,
}: MileageBoundaryParams): MileageBoundary {
  const pictureRegistrationDate = parseDateWithFormat(drvngDstncRgsde);
  const challengeStartDate = parseDateWithFormat(nowDrvngRgsde);

  if (!pictureRegistrationDate || !challengeStartDate) {
    throw new ExpectedError('invalid drvngDstncRgsde or nowDrvngRgsde');
  }
  const daysBetweenRegistrationAndStartDate = differenceInDay(pictureRegistrationDate, challengeStartDate);
  if (
    challengePeriodCase === ChallengePeriodType.ONE_YEAR &&
    challengeCalculationCase === ChallengeCalculationMethodType.REDUCTION_AMOUNT
  ) {
    return {
      seven: stdrDrvngDstnc + nowDrvngDstnc - 3000,
      five: stdrDrvngDstnc + nowDrvngDstnc - 2000,
      three: stdrDrvngDstnc + nowDrvngDstnc - 1000,
      two: stdrDrvngDstnc + nowDrvngDstnc,
      // persistent는 스펙아웃되어 임의의 큰값을 표현
      persistent: Infinity,
    };
  }
  if (challengePeriodCase === ChallengePeriodType.ONE_YEAR && challengeCalculationCase === ChallengeCalculationMethodType.REDUCTION_RATE) {
    return {
      seven: 0.7 * stdrDrvngDstnc + nowDrvngDstnc,
      five: 0.8 * stdrDrvngDstnc + nowDrvngDstnc,
      three: 0.9 * stdrDrvngDstnc + nowDrvngDstnc,
      two: stdrDrvngDstnc + nowDrvngDstnc,
      // persistent는 스펙아웃되어 임의의 큰값을 표현
      persistent: Infinity,
    };
  }
  if (
    challengePeriodCase === ChallengePeriodType.TWO_YEARS_OR_MORE &&
    challengeCalculationCase === ChallengeCalculationMethodType.REDUCTION_AMOUNT
  ) {
    return {
      seven: Math.trunc((stdrDrvngDstnc - 3000) * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      five: Math.trunc((stdrDrvngDstnc - 2000) * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      three: Math.trunc((stdrDrvngDstnc - 1000) * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      two: Math.trunc(stdrDrvngDstnc * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      // persistent는 스펙아웃되어 임의의 큰값을 표현
      persistent: Infinity,
    };
  }
  if (
    challengePeriodCase === ChallengePeriodType.TWO_YEARS_OR_MORE &&
    challengeCalculationCase === ChallengeCalculationMethodType.REDUCTION_RATE
  ) {
    return {
      seven: Math.trunc(0.7 * stdrDrvngDstnc * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      five: Math.trunc(0.8 * stdrDrvngDstnc * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      three: Math.trunc(0.9 * stdrDrvngDstnc * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      two: Math.trunc(stdrDrvngDstnc * (daysBetweenRegistrationAndStartDate / 365) + nowDrvngDstnc),
      // persistent는 스펙아웃되어 임의의 큰값을 표현
      persistent: Infinity,
    };
  }
  if (challengePeriodCase === ChallengePeriodType.TODAY) {
    return {seven: 0, five: 0, three: 0, two: 0, persistent: 0};
  }
  throw new ExpectedError('getChallengeBoundary error');
}

function getChallengePeriodCase(nowDrvngRgsde: string) {
  const today = new Date();
  const challengeStartDate = parseDateWithFormat(nowDrvngRgsde);
  if (!challengeStartDate) {
    throw new ExpectedError('invalid nowDrvngRgsde');
  }
  const challengePeriodDays = differenceInDay(today, challengeStartDate);
  if (!challengePeriodDays) {
    return ChallengePeriodType.TODAY;
  }
  return challengePeriodDays > 0 && challengePeriodDays <= 365 ? ChallengePeriodType.ONE_YEAR : ChallengePeriodType.TWO_YEARS_OR_MORE;
}

function getChallengeCalculationCase(stdrDrvngDstnc: number) {
  return stdrDrvngDstnc >= 0 && stdrDrvngDstnc < 10000
    ? ChallengeCalculationMethodType.REDUCTION_RATE
    : ChallengeCalculationMethodType.REDUCTION_AMOUNT;
}

function getChallengeDDay(drvngDstncRgsde: string, drvngDstncEndde: string) {
  const today = new Date();
  const pictureRegistrationDate = parseDateWithFormat(drvngDstncRgsde);
  const pictureRegistrationEndDate = parseDateWithFormat(drvngDstncEndde);
  if (!pictureRegistrationDate || !pictureRegistrationEndDate) {
    throw new ExpectedError('invalid drvngDstncRgsde or drvngDstncEndde');
  }
  const challengeEndDate = addDate(pictureRegistrationDate, {
    days: -1,
  });
  return pictureRegistrationDate < today && today <= pictureRegistrationEndDate
    ? differenceInDay(today, challengeEndDate) - 365
    : differenceInDay(today, challengeEndDate);
}

function getChallengeTypeHelper({
  challengeBoundary,
  stdrDrvngGbn,
  challengePeriodCase,
  stdrDrvngDstnc,
}: {
  challengeBoundary: MileageBoundary;
  stdrDrvngGbn: string;
  challengePeriodCase: ChallengePeriodType;
  stdrDrvngDstnc: number;
}) {
  return (odometer: number) => {
    if (!stdrDrvngDstnc || stdrDrvngGbn === '01' || challengePeriodCase === ChallengePeriodType.TODAY) {
      return ChallengeType.UPDATE_STATUS;
    }
    if (odometer <= challengeBoundary.seven) {
      return ChallengeType.SEVEN;
    }
    if (odometer <= challengeBoundary.five) {
      return ChallengeType.FIVE;
    }
    if (odometer <= challengeBoundary.three) {
      return ChallengeType.TRREE;
    }
    if (odometer <= challengeBoundary.two) {
      return ChallengeType.TWO;
    }
    if (odometer > challengeBoundary.two) {
      return ChallengeType.CHALLENGE_UNAVAILABLE;
    }
    return ChallengeType.UPDATE_STATUS;
  };
}

function getIsChallenge24Impossible(drvngDstncRgsde: string) {
  const pictureRegistrationDate = parseDateWithFormat(drvngDstncRgsde);
  if (!pictureRegistrationDate) {
    throw new ExpectedError('invalid drvngDstncRgsde');
  }
  const challengeEndDate = addDate(pictureRegistrationDate, {days: -1});
  return challengeEndDate < parseDateISO('2025');
}

function useMileage(vin: string) {
  const {
    data: {
      stdr_drvng_dstnc: stdrDrvngDstnc,
      drvng_dstnc_rgsde: drvngDstncRgsde,
      drvng_dstnc_endde: drvngDstncEndde,
      now_drvng_rgsde: nowDrvngRgsde,
      now_drvng_dstnc: nowDrvngDstnc,
      stdr_drvng_gbn: stdrDrvngGbn,
    },
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.getInfoLink), vin],
    queryFn: () =>
      diApi.ecoMileageController.getInfoLink({
        vin,
      }),
    select: (data: ApiResponseEcoMileageResponseInfoLinkRtnData) => data.data.rtn_data,
  });
  const pictureRegistrationDate = parseDateWithFormat(drvngDstncRgsde);
  const challengeStartDate = parseDateWithFormat(nowDrvngRgsde);
  if (!pictureRegistrationDate || !challengeStartDate) {
    throw new ExpectedError('invalid drvngDstncRgsde');
  }
  const challengeEndDate = addDate(pictureRegistrationDate, {days: -1});

  const challengeDDay = getChallengeDDay(drvngDstncRgsde, drvngDstncEndde);

  const challengePeriodCase = getChallengePeriodCase(nowDrvngRgsde);

  const challengeCalculationCase = getChallengeCalculationCase(stdrDrvngDstnc);

  const challengeBoundary = getChallengeBoundary({
    challengeCalculationCase,
    challengePeriodCase,
    stdrDrvngDstnc,
    nowDrvngDstnc,
    drvngDstncRgsde,
    nowDrvngRgsde,
  });

  const getChallengeType = getChallengeTypeHelper({
    challengeBoundary,
    stdrDrvngGbn,
    challengePeriodCase,
    stdrDrvngDstnc,
  });

  const isChallenge24Impossible = getIsChallenge24Impossible(drvngDstncRgsde);

  return {
    challengeDDay,
    challengeStartDate,
    challengeEndDate,
    challengeBoundary,
    getChallengeType,
    isChallenge24Impossible,
  };
}

export default useMileage;
